import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useVenue } from "@/components/shared/VenueContext";
import { getMemToken } from "@/api/api";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Search, RefreshCcw,
  Save, Eye, Package, ChevronRight, BarChart3, AlertCircle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

async function authFetch(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(getMemToken() ? { Authorization: `Bearer ${getMemToken()}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Error" }));
    throw new Error(err.detail || "Request failed");
  }
  return res.json();
}

const SEVERITY_STYLES = {
  ok: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "OK", icon: CheckCircle2 },
  warning: { color: "bg-amber-100 text-amber-700 border-amber-200", label: "Marge baix", icon: AlertCircle },
  critical: { color: "bg-red-100 text-red-700 border-red-200", label: "Crític", icon: AlertTriangle },
  incomplete: { color: "bg-gray-100 text-gray-600 border-gray-200", label: "Incomplet", icon: AlertCircle },
  no_data: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "Sense dades", icon: Eye },
};

export default function CostReview() {
  const { currentVenue, loading: venueLoading } = useVenue();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all/ok/warning/critical/incomplete
  const [search, setSearch] = useState("");
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [applying, setApplying] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);

  const loadData = async () => {
    if (!currentVenue?.id) return;
    setLoading(true);
    try {
      const result = await authFetch(`/api/cost-review/${currentVenue.id}`);
      setData(result);
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (currentVenue) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVenue]);

  const handleApply = async () => {
    setApplying(true);
    setShowApplyDialog(false);
    try {
      const res = await authFetch(`/api/cost-review/${currentVenue.id}/apply`, { method: "POST" });
      toast.success(`${res.updated} fitxes actualitzades · ${res.skipped} saltades (incompletes)`);
      await loadData();
    } catch (err) {
      toast.error(err.message);
    }
    setApplying(false);
  };

  if (venueLoading || loading) return <QuickLoader />;
  if (!data) return null;

  const sheets = data.sheets || [];
  const filteredSheets = sheets.filter((s) => {
    if (filter !== "all" && s.severity !== filter) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = {
    all: sheets.length,
    ok: sheets.filter((s) => s.severity === "ok").length,
    warning: sheets.filter((s) => s.severity === "warning").length,
    critical: sheets.filter((s) => s.severity === "critical").length,
    incomplete: sheets.filter((s) => s.severity === "incomplete").length,
    no_data: sheets.filter((s) => s.severity === "no_data").length,
  };

  const stats = [
    { label: "Total fitxes", value: data.total_sheets, color: "text-gray-700", bg: "bg-gray-50", icon: Package },
    { label: "Calculats correctament", value: data.complete_sheets, color: "text-emerald-700", bg: "bg-emerald-50", icon: CheckCircle2 },
    { label: "Incomplets (revisar)", value: data.incomplete_sheets, color: "text-amber-700", bg: "bg-amber-50", icon: AlertCircle },
    { label: "Food cost mitjà", value: `${data.average_food_cost_pct}%`, color: "text-blue-700", bg: "bg-blue-50", icon: BarChart3 },
  ];

  return (
    <div className="space-y-5" data-testid="cost-review-page">
      <PageHeader
        title="Revisió de Costos"
        subtitle={`Anàlisi automàtica del cost real de ${data.total_sheets} fitxes tècniques`}
        actions={
          <div className="flex gap-2">
            <Button onClick={loadData} variant="outline" className="gap-2 rounded-xl" data-testid="refresh-cost-review-btn">
              <RefreshCcw className="w-4 h-4" /> Actualitzar
            </Button>
            <Button
              onClick={() => setShowApplyDialog(true)}
              disabled={applying || data.complete_sheets === 0}
              className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl"
              data-testid="apply-costs-btn"
            >
              <Save className="w-4 h-4" /> Aplicar als plats
            </Button>
          </div>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label} className="p-4 border-0 shadow-sm">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{s.label}</p>
                  <p className={`text-xl font-semibold ${s.color}`}>{s.value}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Unmatched ingredients warning */}
      {(data.unmatched_ingredients || []).length > 0 && (
        <Card className="p-4 border-0 shadow-sm bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900 mb-1">
                {data.unmatched_ingredients.length} ingredients sense preu vinculat
              </p>
              <p className="text-sm text-amber-800 mb-2">
                Aquests ingredients apareixen a les fitxes però no tenen preu al catàleg. Crea'ls a la secció "Ingredients" per a un càlcul exacte.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {data.unmatched_ingredients.slice(0, 15).map((u) => (
                  <Badge key={u.name} variant="outline" className="text-xs bg-white">
                    {u.name} <span className="ml-1 text-amber-600 font-medium">×{u.count}</span>
                  </Badge>
                ))}
                {data.unmatched_ingredients.length > 15 && (
                  <Badge variant="outline" className="text-xs bg-white">+{data.unmatched_ingredients.length - 15} més</Badge>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {[
          { key: "all", label: "Totes" },
          { key: "ok", label: "OK" },
          { key: "warning", label: "Marge baix" },
          { key: "critical", label: "Crítics" },
          { key: "incomplete", label: "Incomplets" },
          { key: "no_data", label: "Sense dades" },
        ].map((f) => (
          <Button
            key={f.key}
            size="sm"
            variant={filter === f.key ? "default" : "outline"}
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? "bg-[#7B2D3F] hover:bg-[#5B1D2F]" : ""}
            data-testid={`filter-${f.key}`}
          >
            {f.label} ({counts[f.key]})
          </Button>
        ))}
        <div className="flex-1 max-w-xs ml-auto">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Cercar plat..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="cost-search-input"
            />
          </div>
        </div>
      </div>

      {/* Sheet list */}
      {filteredSheets.length === 0 ? (
        <Card className="p-8 text-center border-dashed">
          <Package className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600">Cap fitxa en aquest filtre</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredSheets.map((s) => {
            const sev = SEVERITY_STYLES[s.severity] || SEVERITY_STYLES.incomplete;
            const Icon = sev.icon;
            return (
              <Card
                key={s.sheet_id}
                className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedSheet(s)}
                data-testid={`sheet-row-${s.sheet_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon className={`w-4 h-4 ${sev.color.split(" ")[1]}`} />
                      <h4 className="font-semibold text-gray-900 truncate">{s.name}</h4>
                      <Badge className={`text-xs ${sev.color}`}>{sev.label}</Badge>
                      {!s.is_complete && (
                        <Badge variant="outline" className="text-xs">
                          {s.unmatched_count > 0 && `${s.unmatched_count} sense preu`}
                          {s.unmatched_count > 0 && s.unit_mismatch_count > 0 && " · "}
                          {s.unit_mismatch_count > 0 && `${s.unit_mismatch_count} unitat KO`}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 mt-1.5 text-xs text-gray-600">
                      <span>PVP <strong className="text-gray-900">{s.selling_price.toFixed(2)}€</strong></span>
                      <span>Cost calculat <strong className="text-gray-900">{s.computed_cost.toFixed(2)}€</strong></span>
                      <span>Cost actual <strong className="text-gray-700">{s.current_cost.toFixed(2)}€</strong></span>
                      {s.is_complete && s.selling_price > 0 && (
                        <>
                          <span>
                            Food cost: <strong className={s.food_cost_pct > 35 ? "text-red-600" : s.food_cost_pct > 30 ? "text-amber-600" : "text-emerald-600"}>{s.food_cost_pct.toFixed(1)}%</strong>
                          </span>
                          <span>
                            Marge: <strong className="text-gray-900">{s.margin_pct.toFixed(1)}%</strong>
                          </span>
                        </>
                      )}
                      {Math.abs(s.delta) > 0.01 && (
                        <span className={s.delta > 0 ? "text-amber-600" : "text-emerald-600"}>
                          {s.delta > 0 ? <TrendingUp className="w-3 h-3 inline mr-0.5" /> : <TrendingDown className="w-3 h-3 inline mr-0.5" />}
                          {s.delta > 0 ? "+" : ""}{s.delta.toFixed(2)}€
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      {selectedSheet && (
        <Dialog open={!!selectedSheet} onOpenChange={(o) => !o && setSelectedSheet(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedSheet.name}</DialogTitle>
              <DialogDescription>
                Desglossament d'ingredients i preus
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-3">
              <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-500">PVP</p><p className="font-semibold">{selectedSheet.selling_price.toFixed(2)}€</p></div>
              <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-500">Cost calculat</p><p className="font-semibold text-[#7B2D3F]">{selectedSheet.computed_cost.toFixed(2)}€</p></div>
              <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-500">Food cost</p><p className="font-semibold">{selectedSheet.food_cost_pct.toFixed(1)}%</p></div>
              <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-500">Marge</p><p className="font-semibold">{selectedSheet.margin_pct.toFixed(1)}%</p></div>
            </div>

            <div className="space-y-1.5">
              {(selectedSheet.breakdown || []).map((b, i) => (
                <div key={`${b.name}-${i}-${b.quantity}`} className={`p-2.5 rounded-lg border ${b.matched ? "bg-emerald-50 border-emerald-100" : "bg-red-50 border-red-100"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{b.name}</p>
                      <p className="text-xs text-gray-600">
                        {b.quantity}{b.unit}
                        {b.matched && b.matched_to && (
                          <span className="text-gray-500"> → {b.matched_to.length > 40 ? b.matched_to.slice(0, 40) + "…" : b.matched_to} ({b.matched_price}€/{b.matched_unit})</span>
                        )}
                      </p>
                      {b.reason === "unit_mismatch" && (
                        <p className="text-xs text-red-600 mt-1">⚠ Unitats incompatibles ({b.unit} → {b.matched_unit}) — no es pot calcular</p>
                      )}
                      {b.reason === "no_price_in_catalog" && (
                        <p className="text-xs text-red-600 mt-1">⚠ No trobat al catàleg d'ingredients</p>
                      )}
                    </div>
                    {b.matched && b.cost_contribution !== undefined && (
                      <Badge variant="outline" className="bg-white">{(b.cost_contribution || 0).toFixed(3)}€</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aplicar costos calculats a les fitxes?</AlertDialogTitle>
            <AlertDialogDescription>
              S'actualitzaran les <strong>{data.complete_sheets}</strong> fitxes amb tots els ingredients vinculats.
              Les <strong>{data.incomplete_sheets}</strong> fitxes incompletes (amb ingredients sense preu o unitats incompatibles) no es modificaran.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel·lar</AlertDialogCancel>
            <AlertDialogAction onClick={handleApply} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              Aplicar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
